import { supporterEapSessionRequestsQuery } from "@/api/reporting";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { StyledLink } from "@/components/link/StyledLink";
import { Badge } from "@/components/ui/Badge";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { formatDate } from "@common/utils/date";
import { Box, QueryHandler } from "@givenwell/components";
import { EapExtraSessionRequest } from "@givenwell/management-api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/reports/$countryCode/eap-requests")({
  component: SupporterEapSessionRequestsPage,
  beforeLoad: () => ({
    breadcrumb: "Supporter EAP requests",
  }),
});

function SupporterEapSessionRequestsPage() {
  const eapRequestsQuery = useQuery(supporterEapSessionRequestsQuery());

  return (
    <PageWrapper title="Supporter EAP Session Requests">
      <Box css={{ spaceY: 16, mt: 16 }}>
        <QueryHandler
          query={eapRequestsQuery}
          success={({ data }) => <EapExtraSessionRequestsTable records={data.requests || []} />}
        />
      </Box>
    </PageWrapper>
  );
}

interface EapSessionsTableProps {
  records: EapExtraSessionRequest[];
}

function EapExtraSessionRequestsTable({ records }: EapSessionsTableProps) {
  return (
    <DataTable>
      <DataTableBody
        rounded="full"
        data={records}
        columns={[
          {
            header: "Supporter",
            cell: (r) => (
              <StyledLink
                to={`/admin/supporters/$supporterId`}
                params={{ supporterId: r.supporter_id }}
              >
                {r.supporter_name}
              </StyledLink>
            ),
          },
          {
            header: "Member ID",
            cell: (r) => r.member_id,
          },
          {
            header: "Number of Requested Sessions",
            cell: (r) => r.number_of_sessions_requested,
          },
          {
            header: "For Period",
            cell: (row) => (
              <span>
                {formatDate(row.period_start_date)} - {formatDate(row.period_end_date)}
              </span>
            ),
          },
          {
            header: "Request Date",
            cell: (row) => formatDate(row.requested_at),
          },
          {
            header: "Status",
            cell: (row) => {
              const color =
                row.request_status == "pending"
                  ? "blue"
                  : row.request_status == "approved"
                    ? "green"
                    : "red";

              return (
                <Badge color={GetColor(row.request_status)}>
                  {row.request_status.charAt(0).toUpperCase() + row.request_status.slice(1)}
                </Badge>
              );
            },
          },
          {
            header: "Reviewed Date",
            cell: (row) => (row.reviewed_at ? formatDate(row.reviewed_at) : "N/A"),
          },
          {
            header: "Reviewed By",
            cell: (row) => (row.reviewer_name ? row.reviewer_name : "N/A"),
          },
          {
            header: "Time to review",
            cell: (row) => {
              const startTime = new Date(row.requested_at);
              const endTime = row.reviewed_at ? new Date(row.reviewed_at) : new Date();

              const days = Math.floor(
                (endTime.getTime() - startTime.getTime()) / (1000 * 3600 * 24),
              );

              return days + " days";
            },
          },
        ]}
        dataKey={(r) => r.request_id}
      />
    </DataTable>
  );

  function GetColor(status: "pending" | "approved" | "declined"): "blue" | "green" | "red" {
    switch (status) {
      case "pending":
        return "blue";
      case "approved":
        return "green";
      case "declined":
        return "red";
    }
  }
}
